import { useState } from "react";
import { motion } from "framer-motion";
import Countdown from "../Countdown/Countdown";

import hyderabadImg from "../../assets/Hyderabad.png";
import chennaiImg from "../../assets/Chennai.png";
import softwareImg from "../../assets/Software.png";
import electricalImg from "../../assets/Electrical.png";
import chicagoImg from "../../assets/Chicago.png";
import vermontImg from "../../assets/Vermont.png";
import usImg from "../../assets/US.png";

import "./StoryBook.css";

const firstChapters = [
  {
    id: "hyderabad",
    number: "01",
    chapter: "Chapter One",
    eyebrow: "Her side of the story",
    title: "Hyderabad",
    text: "Her city of pearls, where families are everything, and every street holds a story. Surrounded by love and shaped by countless little moments, she grew into the woman she was always meant to be. She never imagined her next chapter would begin hundreds of miles away.",
    image: hyderabadImg,
    note: "A Telangana girl with Hyderabad in her heart.",
    align: "right",
  },
  {
    id: "chennai",
    number: "02",
    chapter: "Chapter Two",
    eyebrow: "His side of the story",
    title: "Chennai",
    text: "A city of sea breeze, where mornings begin with the aroma of filter coffee and every home is held together by love. Wrapped in the warmth of family, he grew into the man he was meant to be, never knowing that every wave was quietly carrying him towards her.",
    image: chennaiImg,
    note: "A Tamil boy with Chennai in his soul.",
    align: "left",
  },
];

const finalChapters = [
  {
    id: "chicago",
    number: "04",
    chapter: "Chapter Four",
    title: "Chicago",
    text: "Our first meet at RIU Rooftop - A simple evening that quietly became unforgettable.",
    image: chicagoImg,
    note: "First meet at RIU.",
    align: "left",
  },
  {
    id: "vermont",
    number: "05",
    chapter: "Chapter Five",
    title: "Vermont",
    text: "In the middle of fall colors and mountain roads, one beautiful coincidence brought us close to each other.",
    image: vermontImg,
    note: "A beautiful coincidence.",
    align: "right",
  },
];

function FloatingDecor() {
  return (
    <div className="floatingDecor">
      <span className="floatItem itemOne">♡</span>
      <span className="floatItem itemTwo">✦</span>
      <span className="floatItem itemThree">❀</span>
      <span className="floatItem itemFour">♡</span>

      <span className="leafFall leafA">🍁</span>
      <span className="leafFall leafB">🍂</span>
      <span className="leafFall leafC">🍁</span>
    </div>
  );
}

function ChapterNav() {
  return (
    <nav className="chapterNav">
      <a href="#intro">♡</a>
      <a href="#hyderabad">01</a>
      <a href="#chennai">02</a>
      <a href="#careers-detail">03</a>
      <a href="#chicago">04</a>
      <a href="#vermont">05</a>
      <a href="#save-date">♥</a>
    </nav>
  );
}

function Intro() {
  return (
    <section id="intro" className="storyPanel introPanel">
      <motion.div
        className="introContent"
        initial={{ opacity: 0, y: 45 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
      >
        <p className="introSmall">Anjan & Sahana</p>

        <h1>Our 2 States Story</h1>

        <p className="introSub">arranged by destiny, grown with love</p>

        <div className="introScroll">
          <span>Scroll to begin</span>
          <div />
        </div>
      </motion.div>
    </section>
  );
}

function StoryChapter({ chapter }) {
  const [opened, setOpened] = useState(false);
  const reverse = chapter.align === "left";

  return (
    <section id={chapter.id} className="storyPanel chapterPanel">
      <div className={`chapterGrid ${reverse ? "reverse" : ""}`}>
        <motion.div
          className="chapterText"
          initial={{ opacity: 0, y: 70 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          viewport={{ once: true, amount: 0.35 }}
        >
          <span className="chapterNumber">{chapter.number}</span>

          <p className="chapterLabel">{chapter.chapter}</p>

          {chapter.eyebrow && (
            <p className="chapterEyebrow">{chapter.eyebrow}</p>
          )}

          <h2>{chapter.title}</h2>

          <p>{chapter.text}</p>

          <button
            type="button"
            className="memoryButton"
            onClick={() => setOpened(!opened)}
          >
            {opened ? "Hide little note" : "Tap for little note"}
          </button>
        </motion.div>

        <motion.div
          className="imageStoryCard"
          initial={{
            opacity: 0,
            scale: 0.92,
            rotate: reverse ? -2 : 2,
          }}
          whileInView={{
            opacity: 1,
            scale: 1,
            rotate: 0,
          }}
          transition={{ duration: 1 }}
          viewport={{ once: true, amount: 0.35 }}
          whileHover={{
            scale: 1.025,
            rotate: reverse ? -1.2 : 1.2,
          }}
          onClick={() => setOpened(!opened)}
        >
          <div className="imageGlow" />

          <img
            src={chapter.image}
            alt={`${chapter.title} watercolor doodle`}
          />

          <div className={`memoryNote ${opened ? "show" : ""}`}>
            {chapter.note}
          </div>

          <div className="tapHint">tap image</div>
        </motion.div>
      </div>
    </section>
  );
}

function CareersDetail() {
  return (
    <section id="careers-detail" className="storyPanel careersDetailPanel">
      <motion.div
        className="careerHeader"
        initial={{ opacity: 0, y: 70 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        viewport={{ once: true, amount: 0.35 }}
      >
        <p className="chapterLabel">Chapter Three</p>

        <h2>Our Worlds</h2>

        <p>
          A little code, a little spark, and a lot of conversations that made
          two states, two worlds, and two hearts feel like one.
        </p>

      </motion.div>

      <div className="careerCards">
        <motion.div
          className="miniCareerCard"
          initial={{ opacity: 0, x: -70 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          viewport={{ once: true, amount: 0.35 }}
          whileHover={{ y: -10, rotate: -1 }}
        >
          <img src={softwareImg} alt="Software engineer doodle" />
          <h3>Software Engineer</h3>
        </motion.div>

        <motion.div
          className="connectorHeart"
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 160, damping: 12 }}
          viewport={{ once: true, amount: 0.35 }}
        >
          ♥
        </motion.div>

        <motion.div
          className="miniCareerCard"
          initial={{ opacity: 0, x: 70 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          viewport={{ once: true, amount: 0.35 }}
          whileHover={{ y: -10, rotate: 1 }}
        >
          <img src={electricalImg} alt="Electrical engineer doodle" />
          <h3>Electrical Engineer</h3>
        </motion.div>
      </div>
    </section>
  );
}
function LeapSection() {
  const words = ["we", "decided", "to", "take", "the", "leap"];

  return (
    <section className="storyPanel leapPanel">
      <motion.div
        className="leapContent"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.5 }}
      >
        <motion.p
          className="leapBeginning"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Somewhere between coincidence and certainty...
        </motion.p>

        <div className="leapWords">
          {words.map((word, index) => (
            <motion.span
              key={word}
              initial={{ opacity: 0, scale: 0.3, y: 40 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{
                delay: index * 0.14,
                type: "spring",
                stiffness: 180,
                damping: 12,
              }}
              viewport={{ once: true }}
            >
              {word}
            </motion.span>
          ))}
        </div>

        <motion.div
          className="leapHeart"
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{
            delay: 1,
            type: "spring",
            stiffness: 180,
            damping: 10,
          }}
          viewport={{ once: true }}
        >
          ♥
        </motion.div>

        <motion.p
          className="leapTogether"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          viewport={{ once: true }}
        >
          together
        </motion.p>

        <motion.p
          className="finalChapterInvite"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.55, duration: 0.8 }}
          viewport={{ once: true }}
        >
          Join us as we begin the most beautiful chapter of all...
        </motion.p>
      </motion.div>
    </section>
  );
}
function SaveDate() {
  return (
    <section
      id="save-date"
      className="storyPanel saveDatePanel"
    >
      <motion.div
        className="saveImageWrap"
        initial={{ opacity: 0, x: -70 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
      >
        <img
          src={usImg}
          alt="Anjan and Sahana"
        />
      </motion.div>

      <motion.div
        className="saveCard"
        initial={{ opacity: 0, x: 70 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
      >
       <>
         <p className="saveSmall">Date with Destiny</p>

         <p className="saveSubline">
           From two states to one forever
         </p>
       </>

        <h2 className="saveNames">
          <span className="savePersonName">
            Anjan
          </span>

          <span className="saveHeart">
            ♥
          </span>

          <span className="savePersonName">
            Sahana
          </span>
        </h2>

        <p className="saveDate">
          November 22, 2026
        </p>

        <p className="saveLocation">
          Dallas, Texas
        </p>

        <Countdown />

        <p className="saveMessage">
          Our favorite chapter begins here.
        </p>

        <p className="saveHashtag">
          #ANUkoJAaNmilgaya
        </p>
      </motion.div>
    </section>
  );
}

export default function StoryBook({ fullAccess = false }) {
  return (
    <main className="storyBook">
      <FloatingDecor />
      <ChapterNav />

      <Intro />

      {/* Hyderabad and Chennai — visible to everyone */}
      {firstChapters.map((chapter) => (
        <StoryChapter key={chapter.id} chapter={chapter} />
      ))}

      {/* Chapters 3, 4 and 5 — visible only with LoveA&S */}
      {fullAccess && (
        <>
          <CareersDetail />

          {finalChapters.map((chapter) => (
            <StoryChapter key={chapter.id} chapter={chapter} />
          ))}
        </>
      )}

      {/* Keep your Leap, SaveDate and footer code below this */}

      <LeapSection />

      <SaveDate />

      <button
        type="button"
        className="backTopButton"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        ↑
      </button>
    </main>
  );
}